/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1789689600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Fri Sep 18 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "amplitude",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Amplitude",
          "depth": 0,
          "id": "connect-to-amplitude"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Objects",
          "depth": 1,
          "id": "objects"
        }, {
          "value": "Events",
          "depth": 1,
          "id": "events"
        }, {
          "value": "Cohorts",
          "depth": 1,
          "id": "cohorts"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      a: "a",
      strong: "strong",
      ul: "ul",
      li: "li",
      h3: "h3",
      h4: "h4",
      code: "code"
    }, _provideComponents(), props.components), {EventSyncWarning, NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!EventSyncWarning) _missingMdxReference("EventSyncWarning", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: "Amplitude is a product analytics platform for understanding how customers use your product. Hightouch keeps Amplitude user and group properties current, sends events, and maintains behavioral cohort membership from your warehouse data."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API Reference"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#objects",
                children: _jsx(_components.strong, {
                  children: "Objects"
                })
              })
            }), _jsx(_components.td, {
              children: "Sync records to objects such as users or organizations in Amplitude"
            }), _jsx(_components.td, {
              children: "Upsert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://amplitude.com/docs/analytics/apis/identify-api",
                children: "Identify a user ↗"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#events",
                children: _jsx(_components.strong, {
                  children: "Events"
                })
              })
            }), _jsx(_components.td, {
              children: "Sync records as events to Amplitude. This is often in the form of a track call"
            }), _jsx(_components.td, {
              children: "Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://amplitude.com/docs/analytics/apis/batch-event-upload-api",
                children: "Upload a batch of events ↗"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#cohorts",
                children: _jsx(_components.strong, {
                  children: "Cohorts"
                })
              })
            }), _jsx(_components.td, {
              children: "Sync records to cohorts in Amplitude"
            }), _jsx(_components.td, {
              children: "Add, Remove"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://amplitude.com/docs/apis/analytics/behavioral-cohorts",
                children: "Manage behavioral cohorts ↗"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-amplitude",
        children: "Connect to Amplitude"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), "\nbutton. Select ", _jsx(_components.strong, {
          children: "Amplitude"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to ", _jsx(_components.strong, {
          children: "Amplitude"
        }), " by entering these fields into Hightouch:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "API Key"
          }), ": You can find your API key by following ", _jsx(_components.a, {
            href: "https://www.docs.developers.amplitude.com/analytics/find-api-credentials/",
            children: "these instructions in Amplitude's docs"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Region"
          }), ": This is either a ", _jsx(_components.strong, {
            children: "Standard server"
          }), " or ", _jsx(_components.strong, {
            children: "EU residency server"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've entered these fields, click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". Give your destination a descriptive name and ", _jsx(_components.strong, {
          children: "Finish"
        }), " the destination connection setup."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Amplitude destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull\ndata from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview\npage"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and\nthe Amplitude destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "objects",
        children: "Objects"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch can sync data to either ", _jsx(_components.strong, {
          children: _jsx(_components.a, {
            href: "https://help.amplitude.com/hc/en-us/articles/115002380567-User-properties-and-event-properties",
            children: "User"
          })
        }), " and ", _jsx(_components.strong, {
          children: _jsx(_components.a, {
            href: "https://developers.amplitude.com/docs/group-identify-api",
            children: "Group"
          })
        }), " objects. A dropdown is available to select between ", _jsx(_components.strong, {
          children: "Users"
        }), " and ", _jsx(_components.strong, {
          children: "Groups"
        }), " when configuring an Amplitude sync."]
      }), "\n", _jsxs(_components.p, {
        children: ["This integration only supports the ", _jsx(_components.strong, {
          children: "Upsert"
        }), " mode. In this mode, Hightouch inserts new users or groups into Amplitude and keeps all user attributes up-to-date."]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["For user objects, map at least one of ", _jsx(_components.strong, {
          children: "Amplitude User ID"
        }), " and ", _jsx(_components.strong, {
          children: "Amplitude Device ID"
        }), ". Map both when your model mixes known and anonymous users: rows that carry either identifier are sent, rather than only the rows that carry the one identifier you picked. See ", _jsx(_components.a, {
          href: "https://help.amplitude.com/hc/en-us/articles/206404628-Step-2-Identifying-your-users",
          children: "Amplitude's docs"
        }), " for more on identifying users."]
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch sends one identifier per row. When a row has values for both, the first identifier in the order you mapped them wins, and the other isn't sent for that row. Order the mappings so your preferred identifier comes first, usually ", _jsx(_components.strong, {
          children: "Amplitude User ID"
        }), ", so a known user isn't identified by their device."]
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "Changing which identifier a row resolves to points it at a different Amplitude\nuser. Reordering the mappings, or adding a column that populates a\nhigher-priority identifier, sends later updates to a new user rather than the\none the row previously matched."
        })
      }), "\n", _jsx(_components.p, {
        children: "Rows with no value for any mapped identifier are rejected."
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch matches rows from your model to Amplitude groups by ", _jsx(_components.strong, {
          children: "Group Value"
        }), ". Select the model column that contains these values if you are syncing groups."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["You can sync columns from your source to Amplitude's default and custom fields. User properties only apply on the next event ", _jsx(_components.strong, {
          children: "after"
        }), " the property has been changed, and don't apply retrospectively. For more information, check out ", _jsx(_components.a, {
          href: "https://help.amplitude.com/hc/en-us/articles/115002380567-User-Properties-Event-Properties#applying-user-properties-to-events",
          children: "Amplitude's FAQ"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "delete-behavior",
        children: "Delete behavior"
      }), "\n", _jsx(_components.p, {
        children: "The delete behavior you select dictates what to do when a row no longer appears in your model's query results. You have the following options for Amplitude objects:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Do nothing"
              })
            }), _jsx(_components.td, {
              children: "Keep the customer or group record in Amplitude with all its synced fields"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Clear"
              })
            }), _jsx(_components.td, {
              children: "Remove all the mapped fields, but keep the record in Amplitude"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "events",
        children: "Events"
      }), "\n", _jsx(EventSyncWarning, {}), "\n", _jsxs(_components.p, {
        children: ["Hightouch supports sending events of a given name, for example, ", _jsx(_components.code, {
          children: "Signed Up"
        }), ". Enter this name and choose the model column containing either the user ID or device ID. You can also select the model column containing the event timestamp. If the timestamp field is empty, Hightouch uses the time the event arrives at the server."]
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "Hightouch accepts a normal date time format for the timestamp, and\nautomatically converts it to the format required by Amplitude."
        })
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-1",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch allows you to pass data to the ", _jsx(_components.a, {
          href: "https://developers.amplitude.com/docs/http-api-v2#keys-for-the-event-argument",
          children: "default properties"
        }), " and the custom event properties of an Amplitude event."]
      }), "\n", _jsx(_components.h3, {
        id: "cohorts",
        children: "Cohorts"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch can sync data to Behavioral Cohorts in Amplitude."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-1",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch matches rows from your model to Amplitude users by ", _jsx(_components.strong, {
          children: "User ID"
        }), " or ", _jsx(_components.strong, {
          children: "Amplitude ID"
        }), ". Select the model column that contains these values if you are syncing to cohorts. See ", _jsx(_components.a, {
          href: "https://amplitude.com/docs/apis/analytics/behavioral-cohorts",
          children: "Amplitude's docs"
        }), " for more on behavioral cohorts."]
      }), "\n", _jsx(_components.h4, {
        id: "creating-new-cohorts",
        children: "Creating new Cohorts"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch can ", _jsx(_components.strong, {
          children: "Create a new cohort"
        }), " when syncing to Amplitude cohorts. Hightouch will create a single placeholder user when creating a new cohort, and then sync the mapped identifiers to your newly created cohort."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["For Hightouch platform error codes related to Amplitude, see ", _jsx(_components.a, {
            href: "/developer-tools/error-codes#other-destinations",
            children: "Error codes:\nOther destinations"
          }), "."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
